import { BaseMeiucaService } from './MeiucaService'
import { env, apiEndpoints } from '@/lib/env'
import type { PaginatedResponse } from './pagination'

// Re-exported for backward compatibility with existing importers.
export type { PaginatedResponse, PaginationInfo } from './pagination'

// ---------------------------------------------------------------------------
// Create reservation (booking flow — `BookingPage`)
// ---------------------------------------------------------------------------

export interface ReservationItem {
  courtId: string
  modality: string
  startsAt: string // ISO date string
  endsAt: string // ISO date string
  priceCents: number
}

export type ReservationPaymentMethod = 'pix'

export interface CreateReservationRequest {
  venueId: string
  priceCents: number
  note?: string
  items: ReservationItem[]
  /** Omit for "pay at the venue" (manual); `'pix'` for online Pix payment. */
  paymentMethod?: ReservationPaymentMethod
}

export interface ReservationItemResponse {
  id: string
  reservationId: string
  courtId: string
  customerId: string
  venueId: string
  modality: string
  startsAt: string
  endsAt: string
  priceCents: number
  createdAt: string
  updatedAt: string
}

export interface ReservationResponse {
  id: string
  customerId: string
  venueId: string
  status: string
  paymentStatus: string
  priceCents: number
  note?: string
  createdAt: string
  updatedAt: string
}

export interface CreateReservationResponse {
  reservation: ReservationResponse
  items: ReservationItemResponse[]
}

// ---------------------------------------------------------------------------
// Shared types — list + detail
// ---------------------------------------------------------------------------

export interface VenueInfo {
  name: string
  address?: string
  latitude?: number
  longitude?: number
  logo?: string
  // Not sent by the mobile-app-api reservation surface, kept optional so older
  // consumers keep compiling.
  id?: string
  slug?: string
  city?: string
  state?: string
  zipCode?: string
}

/** Raw `ReservationStatus` values from the backend. */
export type ReservationStatus = 'confirmed' | 'canceled'

/** Raw `ReservationPaymentStatus` values from the backend. */
export type ReservationPaymentStatus =
  | 'unpaid'
  | 'processing'
  | 'paid'
  | 'failed'
  | 'refundProcessing'
  | 'refunded'
  | 'chargeback'
  | 'unknown'

export interface AppReservation {
  id: string
  venue: VenueInfo
  date: string
  status: ReservationStatus
  paymentStatus: ReservationPaymentStatus
  priceCents: number
  note?: string
  createdAt?: string
  paidAt?: string
  checkedInAt?: string
  noShowAt?: string
  canceledAt?: string
}

// ---------------------------------------------------------------------------
// Detail + Pix payment
// ---------------------------------------------------------------------------

export interface ReservationCourt {
  reservationItemId: string
  id: string
  name: string
}

export interface ReservationTimeGroup {
  startsAt: string
  endsAt: string
  courts: ReservationCourt[]
}

export interface ReservationPixDetails {
  paymentId?: string | null
  payload?: string | null
  encodedImage?: string | null
  expirationDate?: string | null
  invoiceUrl?: string | null
}

export interface ReservationPaymentDetailsPayload {
  pix?: ReservationPixDetails | null
}

export interface AppReservationDetail extends AppReservation {
  updatedAt?: string
  timeGroups: ReservationTimeGroup[]
  paymentDetails?: ReservationPaymentDetailsPayload | null
}

/** Response of `GET /me/reservations/{id}/payment-details`. */
export interface ReservationPaymentDetails {
  paymentStatus: ReservationPaymentStatus
  paymentDetails?: ReservationPaymentDetailsPayload | null
}

// ---------------------------------------------------------------------------
// Cancel preview
// ---------------------------------------------------------------------------

export type ReservationCancelResolutionKind =
  | 'canCancelNoRefundDue'
  | 'canCancelAutomaticOnlineRefund'
  | 'canCancelManualFullRefund'
  | 'mustContactArena'

export interface ReservationCancelResolution {
  kind: ReservationCancelResolutionKind
  refundAmountCents?: number
  cancellationFeeCents?: number
}

export interface ReservationCancelPreview {
  reservationId: string
  paidAmountCents: number
  refundPercentage: number
  resolution: ReservationCancelResolution
}

class ReservationService extends BaseMeiucaService {
  constructor() {
    super({
      baseURL: env.API_URL,
      refreshEndpoint: apiEndpoints.auth.refresh,
      cookiePrefix: '', // No prefix for meiuca-app (keeps original behavior)
      debug: env.IS_DEVELOPMENT,
      onAuthenticationError: () => {
        // Use replace to prevent back button issues - redirect to root
        if (typeof window !== 'undefined') {
          window.location.replace('/')
        }
      }
    })
  }

  async createReservation(data: CreateReservationRequest): Promise<CreateReservationResponse> {
    const response = await this.post('/reservations', data)
    return response.data.data
  }

  async listMyReservations(params?: {
    period?: 'upcoming' | 'past'
    limit?: number
    cursor?: string
  }): Promise<PaginatedResponse<AppReservation>> {
    const queryParams = new URLSearchParams()
    queryParams.append('period', params?.period ?? 'upcoming')
    if (params?.limit) {
      queryParams.append('limit', params.limit.toString())
    }
    if (params?.cursor) {
      queryParams.append('cursor', params.cursor)
    }

    // Paginated endpoints encode `PaginatedResponse` directly ({ data, pagination }),
    // so the whole body is the payload — unlike single-object endpoints.
    const response = await this.get<PaginatedResponse<AppReservation>>(
      `/me/reservations?${queryParams.toString()}`
    )
    return response.data
  }

  async getReservationDetail(id: string): Promise<AppReservationDetail> {
    const response = await this.get<{ data: AppReservationDetail }>(
      `/me/reservations/${encodeURIComponent(id)}`
    )
    return response.data.data
  }

  async getReservationPaymentDetails(id: string): Promise<ReservationPaymentDetails> {
    const response = await this.get<{ data: ReservationPaymentDetails }>(
      `/me/reservations/${encodeURIComponent(id)}/payment-details`
    )
    return response.data.data
  }

  async getCancelPreview(id: string): Promise<ReservationCancelPreview> {
    const response = await this.get<{ data: ReservationCancelPreview }>(
      `/me/reservations/${encodeURIComponent(id)}/cancel/preview`
    )
    return response.data.data
  }

  async cancelReservation(id: string): Promise<void> {
    // Body is optional on the backend; cancellation is driven by the path id.
    await this.post(`/me/reservations/${encodeURIComponent(id)}/cancel`, {})
  }
}

// Export singleton instance
export const reservationService = new ReservationService()
